import {useCallback} from 'react'

import {useIssuesDashboardSurfaceStateContext} from '../contexts/IssuesDashboardSurfaceStateContext'
import type {IssuesDashboardSurfacePayload} from '../types/routes-types'
import {useAnalytics} from '@github-ui/use-analytics'
import {getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {isExplicitIssueQuery} from '../helpers/is-explicit-issue-query'

interface UseIssuesDashboardStateType {
  isLoading: boolean
  results: IssuesDashboardSurfacePayload
  currentPage: number
  methods: {
    fetchResults: (params: {q: string; page: number; sidebarLinkId?: string}) => void
    updateAndSubmitQuery: (q: string) => void
  }
}

export function useIssuesDashboardState(payload: IssuesDashboardSurfacePayload): UseIssuesDashboardStateType {
  const {fetchedQuery, currentPage, fetchResults, isLoading} = useIssuesDashboardSurfaceStateContext()
  const {sendAnalyticsEvent} = useAnalytics()

  const emptyResults: IssuesDashboardSurfacePayload = {
    results: [],
    pageInfo: {currentPage: 1, totalPages: 1, totalCount: 0},
  }
  const hasQuery = !!fetchedQuery
  const results = hasQuery ? payload : emptyResults
  const updateAndSubmitQuery = useCallback(
    (q: string) => {
      sendAnalyticsEvent('issues-dashboard.search', 'UPDATE_AND_SUBMIT_QUERY', {
        sort: getSortFromQuery(q),
        // Whether the searched query narrows to issues via an explicit
        // `is:issue`/`type:issue` qualifier. When false the view serves mixed
        // issue + PR content (the opt-in path).
        issueScoped: isExplicitIssueQuery(q),
      })
      fetchResults({q, page: 1})
    },
    [fetchResults, sendAnalyticsEvent],
  )

  return {
    isLoading,
    results,
    currentPage,
    methods: {
      fetchResults,
      updateAndSubmitQuery,
    },
  }
}
